import { useEffect, useMemo, useState } from "react";
import { Printer, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { Product, ProductVariation } from "../../types/Product";
import { formatSlipSize, printItemSlip } from "../../utils/itemSlip";

interface PrintSlipModalProps {
  product: Product | null;
  onClose: () => void;
  onPrinted?: (pdfUrl: string) => void;
}

/**
 * Lets the user pick a variation (size, style, ...) before printing an item slip,
 * the same way the POS asks for a variation before adding to cart.
 */
const PrintSlipModal: React.FC<PrintSlipModalProps> = ({ product, onClose, onPrinted }) => {
  const { t } = useTranslation();
  const { err } = useToast();

  const [variations, setVariations] = useState<ProductVariation[]>([]);
  const [variationTypes, setVariationTypes] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!product) return;

    let cancelled = false;
    setVariations([]);
    setVariationTypes([]);
    setSelected({});
    setLoadError(null);
    setLoading(true);

    BaseApi.get(`/products/${product.id}`)
      .then((res: any) => {
        if (cancelled) return;
        const detail = res?.data?.product || res?.data;
        if (!detail) {
          setLoadError(t("Failed to load product options"));
          return;
        }
        setVariations((detail.variations || []).filter((v: any) => v.isActive !== false));
        setVariationTypes(detail.variationTypes || []);
      })
      .catch((error: any) => {
        if (cancelled) return;
        setLoadError(error?.message || t("Failed to load product options"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [product, t]);

  // Option values per attribute type, e.g. { size: ["52", "54"], style: ["5", "6"] }
  const options = useMemo(() => {
    const usedTypes = Array.from(
      new Set(
        variations.flatMap((v) =>
          Object.entries(v.attributes || {})
            .filter(([, value]) => Boolean(value))
            .map(([key]) => key),
        ),
      ),
    );
    const configured = variationTypes.filter((type) => usedTypes.includes(type));
    const types = configured.length > 0 ? configured : usedTypes;

    return types
      .map((type) => ({
        type,
        values: Array.from(
          new Set(variations.map((v) => v.attributes?.[type]).filter(Boolean) as string[]),
        ),
      }))
      .filter(({ values }) => values.length > 0);
  }, [variations, variationTypes]);

  // Pre-select attributes that only have one possible value
  useEffect(() => {
    const single = Object.fromEntries(
      options.filter(({ values }) => values.length === 1).map(({ type, values }) => [type, values[0]]),
    );
    if (Object.keys(single).length > 0) {
      setSelected((prev) => ({ ...single, ...prev }));
    }
  }, [options]);

  const allSelected = options.every(({ type }) => selected[type]);

  const selectedVariation = useMemo(() => {
    if (!allSelected) return null;
    return (
      variations.find((v) => options.every(({ type }) => v.attributes?.[type] === selected[type])) ||
      null
    );
  }, [allSelected, options, selected, variations]);

  const isAvailable = (type: string, value: string) =>
    variations.some(
      (v) =>
        v.attributes?.[type] === value &&
        options.every(
          ({ type: other }) =>
            other === type || !selected[other] || v.attributes?.[other] === selected[other],
        ),
    );

  const handlePrint = async () => {
    if (!product || !selectedVariation) return;

    setPrinting(true);
    try {
      const url = await printItemSlip({
        name: product.name?.en || product.name?.ar,
        model: product.sku,
        size: formatSlipSize(selectedVariation.attributes),
        barcode: product.id,
      });
      onPrinted?.(url);
      onClose();
    } catch (error: any) {
      err(error?.message || t("Failed to generate slip"));
    } finally {
      setPrinting(false);
    }
  };

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-3 sm:py-4 rounded-t-xl sm:rounded-t-2xl flex items-center justify-between sticky top-0">
          <h3 className="text-lg sm:text-xl font-bold text-white">{t("Select Variation")}</h3>
          <button onClick={onClose} className="text-white hover:text-gray-300 transition-colors">
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          <div className="mb-4">
            <h4 className="text-xl font-bold text-gray-800">{product.name?.en || product.name?.ar}</h4>
            <p className="text-sm text-gray-600">{product.sku}</p>
          </div>

          {loading ? (
            <div className="py-8 text-center text-gray-600">{t("Loading options...")}</div>
          ) : loadError ? (
            <div className="py-6 px-4 rounded-lg bg-red-50 text-red-600 text-sm">{loadError}</div>
          ) : options.length === 0 ? (
            <div className="py-6 px-4 rounded-lg bg-gray-50 text-gray-600 text-sm">
              {t("This product has no variations to choose from.")}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {options.map(({ type, values }) => (
                  <div key={type}>
                    <p className="text-xs font-semibold tracking-wide text-gray-500 mb-2 uppercase">
                      {t("Select")} {type}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {values.map((value) => {
                        const active = selected[type] === value;
                        const available = isAvailable(type, value);
                        return (
                          <button
                            key={`${type}-${value}`}
                            type="button"
                            disabled={!available && !active}
                            className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                              active
                                ? "bg-black text-white border-black"
                                : "bg-white text-gray-700 border-gray-300 hover:border-black"
                            }`}
                            onClick={() =>
                              setSelected((prev) => {
                                const next = { ...prev, [type]: value };
                                // Drop other choices that don't exist together with this one
                                options.forEach(({ type: other }) => {
                                  if (
                                    other !== type &&
                                    next[other] &&
                                    !variations.some(
                                      (v) =>
                                        v.attributes?.[type] === value &&
                                        v.attributes?.[other] === next[other],
                                    )
                                  ) {
                                    delete next[other];
                                  }
                                });
                                return next;
                              })
                            }
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-gray-200 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">{t("Slip size")}</p>
                <p className="font-medium text-gray-800 text-sm">
                  {selectedVariation
                    ? formatSlipSize(selectedVariation.attributes)
                    : t("Please select options")}
                </p>
              </div>
            </>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={loading || !!loadError || !selectedVariation || printing}
              className="px-4 py-2 rounded-lg bg-black text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              {printing ? t("Generating...") : t("Print Slip")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintSlipModal;
